import React from "react";
import {Autocomplete} from "@mui/material";
import {Chip, Colors, IconClose} from "@bounce/shared/ui";
import {IconChevronUp} from "@bounce/shared/ui/assets";
import {TextField} from "../../../../ui/components";
import {autocompleteChevronSx} from "../../../../ui/components/inputStyles";

type Props<T extends string | number> = {
  value: T[];
  options: readonly T[];
  onChange: (next: T[]) => void;
  placeholder: string;
  ariaLabel: string;
  error?: boolean;
  isInvalid?: (option: T) => boolean;
};

const TAG_DELETE_ICON_SIZE = 12;
const TAG_MARGIN = 0.5;

const chipVariant = (invalid: boolean) => (invalid ? "error" : "inherit");

const ChipsSelectInput = <T extends string | number>({
  value,
  options,
  onChange,
  placeholder,
  ariaLabel,
  error = false,
  isInvalid,
}: Props<T>) => {
  const isBlocked = (option: T) => !value.includes(option) && (isInvalid?.(option) ?? false);

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size="small"
      options={[...options]}
      value={value}
      onChange={(_event, next) => onChange(next)}
      getOptionLabel={(option) => String(option)}
      getOptionDisabled={isBlocked}
      popupIcon={<IconChevronUp style={{width: 20, height: 20}} />}
      sx={autocompleteChevronSx}
      renderTags={(selected, getTagProps) =>
        selected.map((option, index) => {
          const {key, onDelete} = getTagProps({index});
          return (
            <Chip
              key={key}
              size_="small"
              variant_={chipVariant(isInvalid?.(option) ?? false)}
              label={String(option)}
              onDelete={onDelete}
              deleteIcon={<IconClose />}
              sx={{
                m: TAG_MARGIN,
                "& .MuiChip-deleteIcon": {
                  width: TAG_DELETE_ICON_SIZE,
                  height: TAG_DELETE_ICON_SIZE,
                  padding: 0,
                  marginLeft: TAG_MARGIN,
                  color: "inherit",
                },
              }}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={value.length === 0 ? placeholder : undefined}
          error={error || value.some((option) => isInvalid?.(option))}
          slotProps={{htmlInput: {...params.inputProps, "aria-label": ariaLabel}}}
          sx={{backgroundColor: Colors.bw.white, borderRadius: "8px"}}
        />
      )}
    />
  );
};

export default ChipsSelectInput;
